import React, { useMemo } from 'react';

import orderBy from 'lodash/orderBy';

import { Section, Grid, View } from '@gogame/components';
import { SidePageV2, FormField, FormFieldGroup, withForm } from '@gogame/form';
import { useProcessAsync } from '@gogame/hooks-ts';
import { usePages } from '@gogame/screens';

import { permission as CreativePermissionId } from 'scenes/Gallery/GalleryPage/constants';
import { permission as TrackerPermissionId } from 'scenes/Trackers/Tracker/constants';
import {
  NAME,
  EMAIL,
  IS_TEST,
} from 'scenes/Users/PartnerUsers/components/PartnerUserForm/constants';
import { PERMISSIONS } from 'scenes/Users/PartnerUsers/components/PartnerUserForm/constants';
import {
  loadActions,
  loadBusinessPartners,
  loadAffiliates,
  loadApplications,
  loadBPAffiliates,
} from 'scenes/Users/PartnerUsers/sources/fields';
import {
  UserPermissions,
  PermissionPickerCategory,
  PermissionPicker,
} from 'scenes/Users/components/UserPermissions';
import {
  CATEGORY_ACTIONS,
  CATEGORY_PAGES,
  CATEGORY_BUSINESS_PARTNERS,
  CATEGORY_AFFILIATES,
  CATEGORY_APPLICATIONS,
} from 'scenes/Users/components/UserPermissions/constants';
import { mapPageToOptions } from 'scenes/Users/utils';

import { userSchema } from './schemas';

const PartnerUsersForm = withForm({
  validationSchema: userSchema,
})(({
  onClose,
  title,
  subTitle,
  loading,
  onExited,
  open,
  entityId,
  values,
  setFieldValue,
}) => {
  const [isLoading, processAsync] = useProcessAsync();

  const loadBPA = async (params) => processAsync(loadBPAffiliates(params));

  const pages = usePages(mapPageToOptions, {
    allowDuplicatePermission: false,
  }).filter((page) =>
    [TrackerPermissionId, CreativePermissionId].includes(page.id)
  );

  const sortedPages = useMemo(() => {
    return orderBy(pages, ['name'], ['asc']);
  }, [pages]);

  const handleBPChange = async (_, businessPartners) => {
    const {
      [CATEGORY_BUSINESS_PARTNERS]: valuesBusinessPartners = [],
      [CATEGORY_AFFILIATES]: valuesAffiliates = [],
    } = values[PERMISSIONS] || {};

    const addedBusinessPartners = businessPartners.filter(
      (id) => !valuesBusinessPartners.includes(id)
    );

    if (addedBusinessPartners.length) {
      const addedAffiliates = await loadBPA({
        businessPartners: addedBusinessPartners,
      });

      if (addedAffiliates.length) {
        setFieldValue(`${PERMISSIONS}.${CATEGORY_AFFILIATES}`, [
          ...valuesAffiliates,
          ...addedAffiliates.filter((bp) => !valuesAffiliates.includes(bp)),
        ]);
      }
      return;
    }

    const removedBusinessPartners = valuesBusinessPartners.filter(
      (id) => !businessPartners.includes(id)
    );

    if (removedBusinessPartners.length) {
      const removedAffiliates = await loadBPA({
        businessPartners: removedBusinessPartners,
      });

      if (removedAffiliates.length) {
        setFieldValue(`${PERMISSIONS}.${CATEGORY_AFFILIATES}`, [
          ...valuesAffiliates.filter((bp) => !removedAffiliates.includes(bp)),
        ]);
      }
    }
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.TextField
                padding={0}
                label={'Full name'}
                required={true}
                fixAutoComplete={true}
                name={NAME}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                padding={0}
                label={'Email'}
                fixAutoComplete={true}
                name={EMAIL}
                required={true}
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.Checkbox name={IS_TEST} label={'Test'} padding={0} />
            </Grid>
          </Grid>
        </Section>
        <Section
          title={'Permissions'}
          fullHeight={true}
          isCollapsable
          initialOpen={true}
        >
          <View loading={isLoading}>
            <FormFieldGroup name={PERMISSIONS}>
              <UserPermissions>
                <PermissionPickerCategory
                  id={CATEGORY_ACTIONS}
                  name={CATEGORY_ACTIONS}
                  label={'Actions'}
                  source={loadActions}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_PAGES}
                  name={CATEGORY_PAGES}
                  label={'Pages'}
                  options={sortedPages}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_AFFILIATES}
                  name={CATEGORY_AFFILIATES}
                  label={'Affiliates'}
                  source={loadAffiliates}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_BUSINESS_PARTNERS}
                  name={CATEGORY_BUSINESS_PARTNERS}
                  label={'Business Partners'}
                  source={loadBusinessPartners}
                >
                  <PermissionPicker onChange={handleBPChange} />
                </PermissionPickerCategory>
                <PermissionPickerCategory
                  id={CATEGORY_APPLICATIONS}
                  name={CATEGORY_APPLICATIONS}
                  label={'Applications'}
                  source={loadApplications}
                >
                  <PermissionPicker />
                </PermissionPickerCategory>
              </UserPermissions>
            </FormFieldGroup>
          </View>
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default PartnerUsersForm;
